import React, { useState } from "react";
import { Box, Typography } from "@mui/material";

import List from "../../../../../components/List";
import { BiometricDeviceInfoPanel } from "./BiometricDeviceInfoPanel";
import { BiometricDeviceCreateEdit } from "./BiometricDeviceCreateEdit";
import { BiometricDeviceStatus } from "../../../../../constants/biometricDevice";
import { IBiometricDevice } from "../../../../../interfaces/BiometricDeviceInterface";

interface BiometricDeviceListProps {
  devices: IBiometricDevice[];
  isLoading?: boolean;
}

// eslint-disable-next-line import/prefer-default-export
export function BiometricDevicesList({
  devices,
  isLoading
}: BiometricDeviceListProps): React.JSX.Element {
  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<IBiometricDevice | null>(null);

  const handleSuccess = () => {
    setShowCreateEditPanel(false);
    setSelectedDevice(null);
  };

  return (
    <Box height="100%">
      <List<IBiometricDevice>
        automation="biometric-device-list"
        data={devices}
        isLoading={isLoading}
        rowHeight={55}
        title={
          <Typography variant="h6" fontWeight={500}>
            Biometric Devices
          </Typography>
        }
        onCreateNew={() => setShowCreateEditPanel(true)}
        createLabel="Add Device"
        isResponsive
        activeRow={selectedDevice?.id}
        withoutSearch
        onRowClick={(row) => {
          setSelectedDevice(selectedDevice?.id === row.id ? null : (row as IBiometricDevice));
        }}
        columns={[
          {
            key: "name",
            label: "Device Name",
            sortable: true,
            formatter: (row: IBiometricDevice) => (
              <Typography variant="body2" fontWeight={500}>
                {row.name}
              </Typography>
            )
          },
          {
            key: "serialNumber",
            label: "Serial Number",
            sortable: true,
            formatter: (row: IBiometricDevice) => (
              <Typography variant="body2">{row?.serialNumber}</Typography>
            )
          },
          {
            key: "ip",
            label: "IP Address",
            formatter: (row: IBiometricDevice) => <Typography variant="body2">{row?.ip}</Typography>
          },
          {
            key: "location",
            label: "Location",
            formatter: (row: IBiometricDevice) => (
              <Typography variant="body2">{row?.location || "-"}</Typography>
            )
          },
          {
            key: "isOnline",
            label: "Online Status",
            formatter: (row: IBiometricDevice) => (
              <Typography
                variant="body2"
                sx={{ color: row?.isOnline ? "success.main" : "text.secondary" }}
              >
                {row?.isOnline ? "Online" : "Offline"}
              </Typography>
            )
          },
          {
            key: "status",
            label: "Status",
            formatter: (row: IBiometricDevice) => (
              <Typography
                variant="body2"
                sx={{
                  color:
                    row?.status === BiometricDeviceStatus.ACTIVE ? "success.main" : "error.main",
                  textTransform: "capitalize"
                }}
              >
                {row?.status || "-"}
              </Typography>
            )
          }
        ]}
      />

      {selectedDevice && (
        <BiometricDeviceInfoPanel
          device={selectedDevice}
          onClose={() => setSelectedDevice(null)}
          onEdit={() => setShowCreateEditPanel(true)}
        />
      )}

      {showCreateEditPanel && (
        <BiometricDeviceCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedDevice(null);
          }}
          device={selectedDevice}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
